import { collectionOptions, localOnlyCollectionOptions } from "@tanstack/db";
import { hashKey, type QueryClient } from "@tanstack/query-core";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { withRequestDeadline } from "@softmax/observatory-client/request-signal";
import type {
  ReplayLaunch,
  ReplayLaunchIdentity,
  ReplayReadiness,
} from "./replay-session-records";
import { waitForReplayReadiness } from "./wait-for-replay-readiness";

const replaySessionReadyDeadlineMs = 120_000;

export function replayLaunchCollection(identity: ReplayLaunchIdentity) {
  const id = hashKey(["coworld-replay", "launch", identity]);
  return collectionOptions(id, () => ({
    ...localOnlyCollectionOptions<ReplayLaunch>({
      id,
      getKey: (launch) => launch.id,
    }),
    gcTime: 1,
  }));
}

export function replayReadinessCollection({
  identity,
  launch,
  onHealthPoll,
}: Readonly<{
  identity: ReplayLaunchIdentity;
  launch: ReplayLaunch;
  onHealthPoll?: ((healthPollCount: number) => void) | undefined;
}>) {
  const queryKey = ["coworld-replay", "readiness", identity, launch] as const;
  return collectionOptions(hashKey(queryKey), (db) => ({
    ...queryCollectionOptions<ReplayReadiness>({
      queryClient: db.requireDependency<QueryClient>("queryClient"),
      queryKey,
      queryFn: async ({ signal }) => {
        if (launch.ready)
          return [
            {
              id: "readiness",
              readyAt: launch.responseReceivedAt,
              healthPollCount: 0,
            },
          ];

        const readinessSignal = withRequestDeadline(
          signal,
          replaySessionReadyDeadlineMs,
        );
        const readiness = await waitForReplayReadiness({
          viewerUrl: launch.viewer_url,
          elevatedPrivileges: identity.elevatedPrivileges,
          signal: readinessSignal,
          onHealthPoll,
        }).catch((error: unknown) => {
          readinessSignal.throwIfAborted();
          throw error;
        });
        return [{ id: "readiness", ...readiness }];
      },
      getKey: (readiness) => readiness.id,
      retry: false,
      staleTime: Infinity,
      gcTime: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }),
    // Native zero retains inactive sources; this viewer's polling must stop on unmount.
    gcTime: 1,
  }));
}
